import { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'components'
import { useTranslation } from 'react-i18next'
import { listActivity } from 'service/AdminPanelService'
import styles from '../AdminFullPage.module.scss'

function formatDate(value) {
  if (!value) return ''
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return String(value)
    return d.toLocaleString('ru-RU')
  } catch (e) {
    return String(value)
  }
}

function toCsv(rows, columns) {
  const header = columns.map((c) => `"${c.label}"`).join(',')
  const body = rows
    .map((r) =>
      columns
        .map((c) => `"${String(c.get(r) ?? '').replace(/"/g, '""')}"`)
        .join(','),
    )
    .join('\n')
  return `${header}\n${body}`
}

function AuditTab() {
  const { t } = useTranslation('common')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    actor_id: '',
    date_from: '',
    date_to: '',
    q: '',
    limit: 200,
  })

  const load = useCallback(() => {
    setLoading(true)
    const params = {}
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v != null) params[k] = v
    })
    listActivity(params)
      .then((res) => {
        const data = res?.data || res
        setItems(Array.isArray(data) ? data : data?.results || [])
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [filters])

  useEffect(() => {
    load()
  }, [load])

  const exportCsv = () => {
    const columns = [
      { label: t('admin.fullPage.audit_th_when'), get: (r) => formatDate(r.created_at || r.timestamp) },
      { label: t('admin.fullPage.audit_th_who'), get: (r) => r.actor_username || r.actor || '' },
      { label: t('admin.fullPage.audit_th_action'), get: (r) => r.action || '' },
      { label: t('admin.fullPage.audit_th_module'), get: (r) => r.module || '' },
      { label: t('admin.fullPage.audit_th_object'), get: (r) => r.target_repr || r.target || '' },
      { label: t('admin.fullPage.audit_th_ip'), get: (r) => r.ip_address || r.ip || '' },
    ]
    const csv = toCsv(items, columns)
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit_${Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <h2 className={styles.section_title}>{t('admin.fullPage.audit_title')}</h2>

      <div className={styles.toolbar}>
        <input
          className="unv-input"
          placeholder={t('admin.fullPage.audit_th_module')}
          value={filters.module}
          onChange={(e) =>
            setFilters((s) => ({ ...s, module: e.target.value }))
          }
        />
        <input
          className="unv-input"
          placeholder={t('admin.fullPage.audit_th_action')}
          value={filters.action}
          onChange={(e) =>
            setFilters((s) => ({ ...s, action: e.target.value }))
          }
        />
        <input
          className="unv-input"
          type="date"
          value={filters.date_from}
          onChange={(e) =>
            setFilters((s) => ({ ...s, date_from: e.target.value }))
          }
        />
        <input
          className="unv-input"
          type="date"
          value={filters.date_to}
          onChange={(e) =>
            setFilters((s) => ({ ...s, date_to: e.target.value }))
          }
        />
        <input
          className="unv-input"
          placeholder={t('common.search')}
          value={filters.q}
          onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))}
        />
        <button type="button" className="unv-btn unv-btn--ghost" onClick={load}>
          {t('common.refresh')}
        </button>
        <button type="button" className="unv-btn" onClick={exportCsv}>
          {t('admin.fullPage.audit_export_csv')}
        </button>
      </div>

      {loading ? (
        <div className={styles.spinner_box}>
          <Spinner.Center />
        </div>
      ) : items.length === 0 ? (
        <div className="unv-empty">{t('admin.fullPage.audit_no_records')}</div>
      ) : (
        <table className="unv-table">
          <thead>
            <tr>
              <th>{t('admin.fullPage.audit_th_when')}</th>
              <th>{t('admin.fullPage.audit_th_who')}</th>
              <th>{t('admin.fullPage.audit_th_action')}</th>
              <th>{t('admin.fullPage.audit_th_module')}</th>
              <th>{t('admin.fullPage.audit_th_object')}</th>
              <th>{t('admin.fullPage.audit_th_ip')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, i) => (
              <tr key={row.id || i}>
                <td>{formatDate(row.created_at || row.timestamp)}</td>
                <td>{row.actor_username || row.actor || '—'}</td>
                <td>{row.action || '—'}</td>
                <td>{row.module || '—'}</td>
                <td>{row.target_repr || row.target || '—'}</td>
                <td>{row.ip_address || row.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default AuditTab
